import { useState, useEffect, useRef } from 'react';
import { Activity, Clock, Users } from 'lucide-react';
import { useOrgTimezone, formatTimeInTz } from '@/utils/timezone';
import jobService from '../../services/jobs';
import type { JobLiveStatus, OperatorLiveStatus } from '../../types/jobs';
import { toNum, toNumOr } from '@/utils/numeric';

interface JobLivePanelProps {
  jobId: number;
  isActive: boolean;
  onUpdate?: (status: JobLiveStatus) => void;
}

const POLL_INTERVAL_MS = 30_000;

function JobLivePanel({ jobId, isActive, onUpdate }: JobLivePanelProps) {
  const orgTz = useOrgTimezone();
  const [liveStatus, setLiveStatus] = useState<JobLiveStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const consecutiveErrors = useRef(0);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await jobService.getLiveStatus(jobId);
      if (res.success) {
        consecutiveErrors.current = 0;
        setLiveStatus(res.data);
        onUpdate?.(res.data);
      }
    } catch (e: any) {
      // 401 is handled transparently by the Axios interceptor (token refresh + retry).
      // If we still land here it means the refresh also failed and the interceptor
      // has already redirected to /login — no need to log it again.
      if (e?.status !== 401) {
        consecutiveErrors.current += 1;
        if (consecutiveErrors.current <= 3) {
          console.error('Live status poll error', e);
        }
        // Stop polling after 3 consecutive non-auth failures
        if (consecutiveErrors.current >= 3) {
          clearInterval(timer.current);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      consecutiveErrors.current = 0;
      fetchStatus();
      timer.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isActive]);

  if (!liveStatus) return null;

  const formatTime = (mins: number | null) => {
    if (mins == null) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const statusDotColor = (status: string) => {
    if (status === 'in_progress') return 'bg-green-500 animate-pulse';
    if (status === 'paused') return 'bg-orange-400';
    if (status === 'completed') return 'bg-neutral-300';
    return 'bg-blue-400';
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-indigo-600" />
          <span className="font-medium text-sm text-neutral-800">Live Status</span>
        </div>
        {loading && <span className="text-xs text-neutral-400">Updating...</span>}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="text-xs text-neutral-500">Coverage</p>
          <p className="text-lg font-bold text-indigo-700">
            {toNum(liveStatus.coverage_pct) != null
              ? `${toNumOr(liveStatus.coverage_pct).toFixed(1)}%`
              : liveStatus.covered_area_ha != null
                ? `${liveStatus.covered_area_ha.toFixed(2)} ha`
                : '—'}
          </p>
          {liveStatus.covered_area_ha != null && (
            <p className="text-[10px] text-neutral-500">
              {liveStatus.covered_area_ha.toFixed(2)}
              {liveStatus.target_area_ha != null ? ` / ${liveStatus.target_area_ha.toFixed(2)}` : ''} ha
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-neutral-500">Elapsed</p>
          <p className="text-lg font-bold text-neutral-800">{formatTime(liveStatus.elapsed_minutes)}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500">Est. Remaining</p>
          <p className="text-lg font-bold text-neutral-800">{formatTime(liveStatus.estimated_remaining_minutes)}</p>
        </div>
      </div>

      {/* Operators */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Users className="h-4 w-4 text-neutral-500" />
          <span className="text-xs font-medium text-neutral-600">Operators</span>
        </div>
        <div className="space-y-2">
          {liveStatus.operators.map((op: OperatorLiveStatus) => (
            <div key={op.user_id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${statusDotColor(op.status)}`} />
                <span className="text-neutral-700">{op.first_name} {op.last_name}</span>
              </div>
              <div className="text-xs text-neutral-400 flex items-center gap-2">
                <span>{op.waypoint_count} pts</span>
                {op.latest_position && (
                  <span>
                    <Clock className="h-3 w-3 inline mr-0.5" />
                    {formatTimeInTz(op.latest_position.recorded_at, orgTz)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default JobLivePanel;
